import type { PolicyFormFormikState } from './policy-form.interface';
import type { FormikHelpers } from 'formik';

import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button } from 'components/buttons/button/button';
import { InputField } from 'components/fields/input-field/input-field';
import { TextEditor } from 'components/fields/text-editor/text-editor';
import { GrayPanel } from 'components/panels/gray-panel/gray-panel';
import { Container } from 'components/template/container/container';
import { FixedHeader } from 'components/template/fixed-header/fixed-header';
import { FormBody } from 'components/template/form-body/form-body';
import { FormHeader } from 'components/template/form-header/FormHeader';
import { ConfirmationModal } from 'components/template/modals/confirmation-modal';
import { Spinner } from 'components/template/spinner/spinner';
import { PermissionsContext } from 'contexts';
import { Form, Formik } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import ReportService from 'services/reports.service';
import events from 'shared/constants/events';
import { ASSIGN_AGENCY_MODAL, MODAL_POLICY } from 'shared/constants/modals';
import { validateField } from 'shared/utils';
import CheckIfFormIsDirty from 'shared/utils/checkIfFormIsDirty';
import { useAppDispatch, useAppSelector } from 'store';
import { clearSelectedAgencies } from 'store/agencies/agencies.slice';
import { clearSelectedAgency as clearSelectedAgencyAction } from 'store/agencies/agencies.slice';
import { closeModal, openModal } from 'store/modals/modals.slice';
import {
  removePolicyFromStore,
  setGuidingPrincipleDescription,
} from 'store/policies/policies.slice';
import {
  createPolicyThunk,
  removePolicyThunk,
  updatePolicyThunk,
} from 'store/policies/policies.thunks';
import { vocabulary } from 'vocabulary/german';

import { PrioritiesForm } from './priorities-form/priorities-form';

const reportsService = new ReportService();

export const PolicyForm = () => {
  const dispatch = useAppDispatch();
  const { permissions } = useContext(PermissionsContext);
  const currentYearFrozen = useAppSelector(
    (state) => state.years.currentYearFrozen,
  );
  const policies = useAppSelector((state) => state.policies);
  const agencies = useAppSelector((state) => state.agencies);
  const isPending = useAppSelector(
    (state) =>
      state.policies.isUpdatePolicyThunkPending ||
      state.policies.isGetPolicyThunkPending,
  );
  const [locked, setLocked] = useState(false);
  const [focusPosition, setFocusPosition] = useState(0);
  const setFieldValueRef = useRef<
    ((field: string, value: unknown) => void) | null
  >(null);

  const initialValues: PolicyFormFormikState = {
    id: policies.current?.id ?? null,
    title: policies.current?.title ?? '',
    guidingPrincipleDescription:
      policies.current?.guidingPrincipleDescription ?? '',
    Priorities: policies.current?.Priorities ?? [],
  };

  const formKey = String(policies.current?.id ?? 'new');

  const savePolicy = (values: PolicyFormFormikState) => {
    const updatedPriorities = values.Priorities.map((priority, priorityIdx) => {
      const updatedMidtermGoals = (priority.MidtermGoals || []).map(
        (midtermGoal, midtermGoalIdx) => {
          let agenciesList = (midtermGoal.Agencies || []).map(
            (agency) => agency.id ?? agency,
          );

          const override = agencies?.assigned.find(
            (a) =>
              a.assignedTo.priorityIndex === priorityIdx &&
              a.assignedTo.midTermGoalIndex === midtermGoalIdx,
          );

          if (override) {
            agenciesList = override.selectedAgencies;
          }

          return { ...midtermGoal, Agencies: agenciesList };
        },
      );

      return { ...priority, MidtermGoals: updatedMidtermGoals };
    });

    const updatedValues = {
      ...values,
      Priorities: updatedPriorities,
      guidingPrincipleDescription: values.guidingPrincipleDescription,
    };

    if (updatedValues.id) {
      dispatch(updatePolicyThunk({ ...updatedValues, id: updatedValues.id }));
    } else {
      dispatch(createPolicyThunk(updatedValues));
    }

    dispatch(clearSelectedAgencies());
  };

  const renderSaveButton = () => {
    if (permissions.remove === true && !currentYearFrozen) {
      return (
        <Button type={'submit'} text={vocabulary.buttons.save} id="saveBtn" />
      );
    }
  };

  const renderDeleteButton = (id: number | null) => {
    if (!id) {
      return null;
    }

    if (permissions.remove === true && !currentYearFrozen) {
      return (
        <Button
          className={'delete-button'}
          onClick={() => handleSubmitDelete(id)}
          text={vocabulary.buttons.delete}
        />
      );
    }
  };

  const handleSubmitDelete = (id: number) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmationModal onClose={onClose} onOk={() => handleDelete(id)} />
        );
      },
    });
  };

  const handleDelete = (id: number) => {
    dispatch(removePolicyThunk(id));
    dispatch(clearSelectedAgencies());
    dispatch(removePolicyFromStore());
    dispatch(closeModal({ name: MODAL_POLICY }));
  };

  const renderReportsButton = (id: number | null, type: 'pdf' | 'docx') => {
    if (!id) {
      return null;
    }

    if (permissions.printReport === true) {
      return (
        <Button
          onClick={() => downloadShortPolicyReportByPolicyID(id, type)}
          text={
            type === 'pdf'
              ? vocabulary.buttons.reports.downloadReportPdf
              : vocabulary.buttons.reports.downloadReportDocx
          }
        />
      );
    }
  };

  const downloadShortPolicyReportByPolicyID = (
    policyId: number,
    type: 'pdf' | 'docx',
  ) => {
    setLocked(true);

    reportsService.downloadShortPolicyReportByPolicyID({ policyId, type });
  };

  const handleAssignAgencies = (assignedToData) => {
    dispatch(openModal({ name: ASSIGN_AGENCY_MODAL, data: assignedToData }));
  };

  const clearSelectedAgency = (priorityIndex: number, mtgIndex: number) => {
    dispatch(
      clearSelectedAgencyAction({
        priorityIndex,
        midTermGoalIndex: mtgIndex,
      }),
    );
  };

  useEffect(() => {
    const unlock = () => setLocked(false);

    document.body.addEventListener(events.UNLOCK.name, unlock);

    return () => {
      document.body.removeEventListener(events.UNLOCK.name, unlock);
    };
  }, []);

  const editor = useEditor({
    extensions: [StarterKit],
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      dispatch(setGuidingPrincipleDescription(html));
      setFieldValueRef.current?.('guidingPrincipleDescription', html);
      setFocusPosition(editor.state.selection.from);
    },
  });

  return (
    <Formik
      key={formKey}
      enableReinitialize={false}
      initialValues={initialValues}
      onSubmit={(values, helpers: FormikHelpers<PolicyFormFormikState>) => {
        savePolicy(values);
        helpers.resetForm({
          values,
        });
      }}
    >
      {({ errors, touched, values, dirty, setFieldValue }) => {
        setFieldValueRef.current = setFieldValue;
        return (
          <Form>
            <CheckIfFormIsDirty dirty={dirty} />
            <FixedHeader>
              <FormHeader
                renderSave={renderSaveButton()}
                renderDelete={renderDeleteButton(initialValues.id)}
                renderReport={renderReportsButton(initialValues.id, 'pdf')}
                renderDocxReport={renderReportsButton(initialValues.id, 'docx')}
              />
              <Container>
                <div className={'field-wrapper'}>
                  <InputField
                    name={'title'}
                    className={'form__field_big'}
                    placeholder={
                      vocabulary.forms.policy.placeholders.newPolicyArea
                    }
                    validate={validateField}
                  />
                  {errors.title && touched.title && (
                    <p className={'field-error'}>{errors.title}</p>
                  )}
                </div>
              </Container>
            </FixedHeader>

            <FormBody>
              <GrayPanel text={vocabulary.forms.policy.description} first />

              <Container>
                <div className={'field-wrapper'}>
                  <div className="policies-editor-wrapper">
                    <TextEditor
                      position={focusPosition}
                      commentText={values.guidingPrincipleDescription || ''}
                      editor={editor}
                    />
                  </div>
                  {errors.guidingPrincipleDescription &&
                    touched.guidingPrincipleDescription && (
                      <p className={'field-error'}>
                        {errors.guidingPrincipleDescription}
                      </p>
                    )}
                </div>
              </Container>

              <PrioritiesForm
                values={values}
                handleAssignAgencies={handleAssignAgencies}
                clearSelectedAgency={clearSelectedAgency}
              />
            </FormBody>
            {isPending && <Spinner isFullSize />}
            {locked && (
              <Spinner
                isFullSize
                message={vocabulary.preloader.waitForReport}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
